/**
 * Field Notes Component
 * 
 * Typed observation notes with GPS tagging and categories.
 * Supports inline create, edit, delete with expandable list.
 */

import React, { useCallback, useState } from 'react';
import {
  View, Text, Pressable, ActivityIndicator,
  TextInput, Modal, ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, space, text, icon } from '../../../shared/tokens/mobile';
import { FIELD_NOTE_CATEGORIES, FieldNote, FieldNoteCategory } from '../../../shared/utils/api';
import { useFieldNotes } from '../../../hooks/useFieldNotes';
import { styles } from './fieldNotesStyles';

interface FieldNotesProps {
  portfolioId: number;
  opportunityId: number;
}

const CATEGORY_ICONS: Record<string, keyof typeof Ionicons.glyphMap> = {
  observation: 'eye-outline',
  condition: 'alert-circle-outline',
  boundary: 'resize-outline',
  access: 'car-outline',
  water: 'water-outline',
  timber: 'leaf-outline',
  soil: 'layers-outline',
  wildlife: 'paw-outline',
  other: 'ellipsis-horizontal-outline',
};

const CATEGORY_COLORS: Record<string, string> = {
  observation: color.primary,
  condition: color.warning,
  boundary: '#8B5CF6',
  access: '#06B6D4',
  water: '#3B82F6',
  timber: '#16A34A',
  soil: '#92400E',
  wildlife: '#D97706',
  other: color.textMuted,
};

const COLLAPSED_COUNT = 3;

export default function FieldNotes({ portfolioId, opportunityId }: FieldNotesProps) {
  const { notes, loading, saving, error, createNote, updateNote, deleteNote } = useFieldNotes({ portfolioId, opportunityId });

  const [showComposer, setShowComposer] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [editingNote, setEditingNote] = useState<FieldNote | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Composer state
  const [category, setCategory] = useState<FieldNoteCategory>('observation');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const resetComposer = useCallback(() => {
    setCategory('observation');
    setTitle('');
    setBody('');
    setShowComposer(false);
    setEditingNote(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!body.trim()) return;

    if (editingNote) {
      await updateNote(editingNote.id, { category, title: title.trim() || undefined, body: body.trim() });
    } else {
      await createNote({ category, title: title.trim() || undefined, body: body.trim(), tagLocation: true });
    }
    resetComposer();
  }, [body, title, category, editingNote, createNote, updateNote, resetComposer]);

  const handleEdit = useCallback((note: FieldNote) => {
    setEditingNote(note);
    setCategory(note.category);
    setTitle(note.title || '');
    setBody(note.body);
    setShowComposer(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (confirmDeleteId == null) return;
    setDeleting(true);
    await deleteNote(confirmDeleteId);
    setDeleting(false);
    setConfirmDeleteId(null);
  }, [confirmDeleteId, deleteNote]);

  const visibleNotes = expanded ? notes : notes.slice(0, COLLAPSED_COUNT);
  const hasMore = notes.length > COLLAPSED_COUNT;

  const formatTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Ionicons name="document-text-outline" size={icon.md} color={color.textSecondary} />
          <Text style={styles.sectionTitle}>Field Notes</Text>
        </View>
        <ActivityIndicator size="small" color={color.textMuted} style={{ paddingVertical: space.md }} />
      </View>
    );
  }

  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Ionicons name="document-text-outline" size={icon.md} color={color.textSecondary} />
        <Text style={styles.sectionTitle}>Field Notes</Text>
        <Text style={styles.badge}>{notes.length}</Text>
        <View style={{ flex: 1 }} />
        <Pressable style={styles.addBtn} onPress={() => setShowComposer(true)}>
          <Ionicons name="add" size={icon.md} color={color.primary} />
        </Pressable>
      </View>

      {notes.length === 0 && (
        <Pressable style={styles.emptyState} onPress={() => setShowComposer(true)}>
          <Ionicons name="document-text-outline" size={icon.lg} color={color.textMuted} />
          <Text style={styles.emptyText}>Add field observations, notes about conditions, access, boundaries...</Text>
        </Pressable>
      )}

      {visibleNotes.map(note => {
        const catColor = CATEGORY_COLORS[note.category] || color.textMuted;
        const catIcon = CATEGORY_ICONS[note.category] || 'ellipsis-horizontal-outline';
        return (
          <Pressable key={note.id} style={styles.noteCard} onLongPress={() => handleEdit(note)}>
            <View style={styles.noteHeader}>
              <View style={[styles.categoryBadge, { backgroundColor: catColor + '18' }]}>
                <Ionicons name={catIcon} size={12} color={catColor} />
                <Text style={[styles.categoryText, { color: catColor }]}>{note.category}</Text>
              </View>
              <Text style={styles.noteTime}>{formatTimeAgo(note.created_at)}</Text>
              <Pressable onPress={() => setConfirmDeleteId(note.id)} hitSlop={8}>
                <Ionicons name="close-circle-outline" size={16} color={color.textMuted} />
              </Pressable>
            </View>
            {note.title && <Text style={styles.noteTitle}>{note.title}</Text>}
            <Text style={styles.noteBody} numberOfLines={3}>{note.body}</Text>
            <View style={styles.noteMeta}>
              {note.latitude && note.longitude && (
                <View style={styles.metaItem}>
                  <Ionicons name="location-outline" size={11} color={color.textMuted} />
                  <Text style={styles.metaText}>{note.latitude.toFixed(4)}, {note.longitude.toFixed(4)}</Text>
                </View>
              )}
              {note.author_name && (
                <View style={styles.metaItem}>
                  <Ionicons name="person-outline" size={11} color={color.textMuted} />
                  <Text style={styles.metaText}>{note.author_name}</Text>
                </View>
              )}
            </View>
          </Pressable>
        );
      })}

      {hasMore && (
        <Pressable style={styles.toggleBtn} onPress={() => setExpanded(!expanded)}>
          <Text style={styles.toggleText}>{expanded ? 'Show less' : `Show all ${notes.length} notes`}</Text>
          <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={14} color={color.primary} />
        </Pressable>
      )}

      {error && <Text style={styles.errorText}>{error}</Text>}

      {/* Composer Modal */}
      <Modal visible={showComposer} transparent animationType="slide" onRequestClose={resetComposer}>
        <View style={styles.composerOverlay}>
          <View style={styles.composerCard}>
            <View style={styles.composerHeader}>
              <Text style={styles.composerTitle}>{editingNote ? 'Edit Note' : 'New Field Note'}</Text>
              <Pressable onPress={resetComposer} hitSlop={8}>
                <Ionicons name="close" size={icon.lg} color={color.textSecondary} />
              </Pressable>
            </View>

            {/* Category picker */}
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoryRow}>
              {FIELD_NOTE_CATEGORIES.map(cat => {
                const selected = cat === category;
                const catColor = CATEGORY_COLORS[cat] || color.textMuted;
                return (
                  <Pressable
                    key={cat}
                    style={[styles.categoryChip, selected && { backgroundColor: catColor + '20', borderColor: catColor }]}
                    onPress={() => setCategory(cat)}
                  >
                    <Ionicons name={CATEGORY_ICONS[cat] || 'ellipsis-horizontal-outline'} size={14} color={selected ? catColor : color.textMuted} />
                    <Text style={[styles.chipText, selected && { color: catColor, fontWeight: '600' }]}>{cat}</Text>
                  </Pressable>
                );
              })}
            </ScrollView>

            <TextInput
              style={styles.titleInput}
              placeholder="Title (optional)"
              placeholderTextColor={color.textMuted}
              value={title}
              onChangeText={setTitle}
              maxLength={120}
            />

            <TextInput
              style={styles.bodyInput}
              placeholder="Write your observation..."
              placeholderTextColor={color.textMuted}
              value={body}
              onChangeText={setBody}
              multiline
              textAlignVertical="top"
              maxLength={2000}
            />

            <View style={styles.composerFooter}>
              <View style={styles.geoHint}>
                <Ionicons name="location-outline" size={14} color={color.success} />
                <Text style={styles.geoHintText}>GPS location will be tagged automatically</Text>
              </View>
              <Pressable
                style={[styles.saveBtn, !body.trim() && styles.saveBtnDisabled]}
                onPress={handleSave}
                disabled={!body.trim() || saving}
              >
                {saving ? (
                  <ActivityIndicator size="small" color={color.surface} />
                ) : (
                  <Text style={styles.saveBtnText}>{editingNote ? 'Update' : 'Save Note'}</Text>
                )}
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      {/* Delete Confirmation */}
      <Modal visible={confirmDeleteId != null} transparent animationType="fade" onRequestClose={() => setConfirmDeleteId(null)}>
        <View style={styles.confirmOverlay}>
          <View style={styles.confirmCard}>
            <Text style={styles.confirmTitle}>Delete Note</Text>
            <Text style={styles.confirmMessage}>This note will be permanently removed.</Text>
            <View style={styles.confirmActions}>
              <Pressable style={[styles.confirmBtn, styles.confirmCancelBtn]} onPress={() => setConfirmDeleteId(null)} disabled={deleting}>
                <Text style={styles.confirmCancelText}>Cancel</Text>
              </Pressable>
              <Pressable style={[styles.confirmBtn, styles.confirmDeleteBtn]} onPress={handleDeleteConfirm} disabled={deleting}>
                {deleting ? <ActivityIndicator size="small" color={color.surface} /> : <Text style={styles.confirmDeleteText}>Delete</Text>}
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

